import React, { useState, useEffect, useRef, useCallback, useId } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useApp } from "../context/AppContext";
import Logo from "../components/Logo";
import {
  LOGO_CENTER_CHAT_EMPTY,
  LOGO_SIDEBAR_MARK_EXPANDED,
  getPublicAssetUrl
} from "../config/logoSizes";
import MarkdownMessage from "../components/MarkdownMessage";
import UpgradeModal from "../components/UpgradeModal";
import SessionExpiredModal from "../components/SessionExpiredModal";
import NotificationsPanel from "../components/NotificationsPanel";
import { addNotification, unreadCount } from "../utils/notifications";
const fmtDate = (d, t) => {
  const date = new Date(d), now =  new Date();
  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  if (date.toDateString() === now.toDateString()) return t("today");
  if (date.toDateString() === yest.toDateString()) return t("yesterday");
  return date.toLocaleDateString(void 0, { month: "short", day: "numeric" });
};
const fmtTime = (d) => new Date(d).toLocaleTimeString(void 0, { hour: "2-digit", minute: "2-digit" });
const fmtTimer = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const Chat = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const chatLogoInstanceId = useId().replace(/:/g, "");
  const [logoImageEpoch, setLogoImageEpoch] = useState(0);
  const prevPathnameRef = useRef(null);
  const { user, logout, updateMessageCount, theme, toggleTheme } = useApp();
  const [sessions, setSessions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toast, setToast] = useState(null);
  const [renameModal, setRenameModal] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [remainingSecs, setRemainingSecs] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifBadge, setNotifBadge] = useState(0);
  const [composerUnlocked, setComposerUnlocked] = useState(false);
  const chatZoom = 1;
  const chatZoomOrigin = { x: 50, y: 50 };
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const profileRef = useRef(null);
  const messagesWrapRef = useRef(null);
  const chatAreaRef = useRef(null);
  const isAr = i18n.language === "ar";
  const syncNotifBadge = useCallback(() => {
    setNotifBadge(unreadCount());
  }, []);
  const scrollToBottom = useCallback((behavior = "smooth") => {
    const scroller = chatAreaRef.current || messagesWrapRef.current;
    if (!scroller) return;
    if (scroller.scrollTo) {
      scroller.scrollTo({ top: scroller.scrollHeight, behavior });
    } else {
      scroller.scrollTop = scroller.scrollHeight;
    }
  }, []);
  const isAdmin = user?.role === "admin";
  const isPaidHourly = user?.plan?.type === "hourly" && user?.plan?.isActive && user?.plan?.endTime &&  new Date() < new Date(user.plan.endTime);
  const isPaidMonthly = user?.plan?.type === "monthly" && user?.plan?.monthlyExpiry &&  new Date() < new Date(user.plan.monthlyExpiry);
  const isPaid = isPaidHourly || isPaidMonthly;
  const FREE_LIMIT = 10;
  const msgsLeft = isAdmin ? null : isPaid ? null : Math.max(0, FREE_LIMIT - (user?.messageCount || 0));
  useEffect(() => {
    setNotifBadge(unreadCount());
  }, []);
  useEffect(() => {
    const p = location.pathname;
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = p;
    if (p !== "/chat") return;
    if (prev !== null && prev !== "/chat") {
      setLogoImageEpoch((e) => e + 1);
    }
  }, [location.pathname]);
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) setLogoImageEpoch((x) => x + 1);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);
  useEffect(() => {
    if (msgsLeft === 0 && !isPaid && !isAdmin) {
      addNotification({
        type: "free_limit",
        titleAr: "\u0646\u0641\u062F\u062A \u0631\u0633\u0627\u0626\u0644\u0643 \u0627\u0644\u0645\u062C\u0627\u0646\u064A\u0629",
        titleEn: "Free messages exhausted",
        bodyAr: "\u0644\u0642\u062F \u0627\u0633\u062A\u062E\u062F\u0645\u062A \u062C\u0645\u064A\u0639 \u0631\u0633\u0627\u0626\u0644\u0643 \u0627\u0644\u0645\u062C\u0627\u0646\u064A\u0629 \u2014 \u064A\u0645\u0643\u0646\u0643 \u062A\u0631\u0642\u064A\u0629 \u062E\u0637\u062A\u0643 \u0644\u0644\u0627\u0633\u062A\u0645\u0631\u0627\u0631.",
        bodyEn: "You have used all your free messages \u2014 upgrade your plan to continue."
      });
      setNotifBadge(unreadCount());
    }
  }, [msgsLeft, isPaid, isAdmin]);
  useEffect(() => {
    if (!isAdmin && isPaidHourly && user?.plan?.endTime) {
      const s = Math.max(0, Math.floor((new Date(user.plan.endTime) -  new Date()) / 1e3));
      setRemainingSecs(s);
    } else setRemainingSecs(null);
  }, [isAdmin, isPaidHourly, user?.plan?.endTime]);
  useEffect(() => {
    if (remainingSecs === null) return;
    if (remainingSecs <= 0) {
      setRemainingSecs(null);
      setShowExpired(true);
      return;
    }
    timerRef.current = setTimeout(() => setRemainingSecs((s) => s - 1), 1e3);
    return () => clearTimeout(timerRef.current);
  }, [remainingSecs]);
  useEffect(() => {
    if (typeof window !== "undefined" && !window.matchMedia("(pointer: coarse)").matches) {
      setComposerUnlocked(true);
    }
    const fs = localStorage.getItem("taweelai_fontsize");
    if (fs) document.documentElement.style.setProperty("--site-font-size", `${fs}px`);
  }, []);
  useEffect(() => {
    const inputEl = inputRef.current;
    if (!inputEl) return;
    const handleFocus = () => {
      if (messages.length === 0) return;
      setTimeout(() => scrollToBottom("smooth"), 120);
    };
    inputEl.addEventListener("focus", handleFocus);
    return () => inputEl.removeEventListener("focus", handleFocus);
  }, [scrollToBottom, messages.length]);
  useEffect(() => {
    loadSessions();
  }, []);
  useEffect(() => {
    if (messages.length === 0) return;
    const timer = setTimeout(() => scrollToBottom("smooth"), 120);
    return () => clearTimeout(timer);
  }, [messages, typing, scrollToBottom]);
  useEffect(() => {
    if (toast) {
      const t2 = setTimeout(() => setToast(null), 2600);
      return () => clearTimeout(t2);
    }
  }, [toast]);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const onKeyDown = (e) => {
      const scroller = chatAreaRef.current || messagesWrapRef.current;
      if (!scroller) return;
      if (!["ArrowUp", "ArrowDown", "PageUp", "PageDown"].includes(e.key)) return;
      const active = document.activeElement;
      const tag = active?.tagName;
      if (tag === "INPUT" && active !== inputRef.current) return;
      if (active === inputRef.current && tag === "TEXTAREA" && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        const ta = inputRef.current;
        const pos = ta.selectionStart ?? 0;
        const val = ta.value || "";
        const before = val.slice(0, pos);
        const after = val.slice(pos);
        const lineStart = before.lastIndexOf("\n") + 1;
        const nextNl = after.indexOf("\n");
        const lineEnd = nextNl === -1 ? val.length : pos + nextNl;
        const atFirstLine = lineStart === 0;
        const atLastLine = lineEnd === val.length;
        if (e.key === "ArrowUp" && !atFirstLine) return;
        if (e.key === "ArrowDown" && !atLastLine) return;
      }
      e.preventDefault();
      const dy = e.key === "ArrowUp" ? -120 : e.key === "ArrowDown" ? 120 : e.key === "PageUp" ? -420 : 420;
      scroller.scrollBy({ top: dy, behavior: "smooth" });
    };
    window.addEventListener("keydown", onKeyDown, { passive: false });
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  useEffect(() => {
    if (!searchQ.trim()) {
      setFiltered(sessions);
      return;
    }
    const q = searchQ.toLowerCase();
    setFiltered(sessions.filter((s) => (s.title || "").toLowerCase().includes(q) || (s.lastMessage || "").toLowerCase().includes(q)));
  }, [searchQ, sessions]);
  const loadSessions = async () => {
    try {
      const r = await axios.get("/api/chat/sessions");
      setSessions(r.data.sessions || []);
    } catch {
    }
  };
  const loadSession = async (id) => {
    try {
      const r = await axios.get(`/api/chat/session/${id}`);
      setMessages(r.data.messages || []);
      setActiveId(id);
      setSidebarOpen(false);
    } catch {
      showToast("error", t("error"));
    }
  };
  const newChat = () => {
    setMessages([]);
    setActiveId(null);
    setInput("");
    setSidebarOpen(false);
  };
  const showToast = (type, msg) => setToast({ type, msg });
  const sendMessage = useCallback(async (text) => {
    const txt = (text || input).trim();
    if (!txt) return;
    if (!isAdmin && !isPaid && msgsLeft <= 0) {
      setShowUpgrade(true);
      return;
    }
    setInput("");
    const tmpId = `t-${Date.now()}`;
    setMessages((prev) => [...prev, { _id: tmpId, role: "user", content: txt, timestamp: ( new Date()).toISOString() }]);
    setTyping(true);
    try {
      // Streaming on by default (ChatGPT-like typing). Set REACT_APP_USE_STREAM="0" to disable.
      const useStream = process.env.REACT_APP_USE_STREAM !== "0";
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
      if (useStream) {
        // This is the only call that bypasses the axios interceptor, so it must attach the
        // token itself: iOS WKWebView blocks the cross-site auth cookie (capacitor://localhost
        // origin), leaving the Bearer header as the only credential that survives there.
        const authToken = localStorage.getItem("auth_token");
        const streamRes = await fetch(`${API_URL}/api/chat/message-stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
          },
          credentials: "include",
          body: JSON.stringify({ message: txt, sessionId: activeId })
        });
        if (!streamRes.ok) {
          const errData = await streamRes.json().catch(() => ({}));
          if (streamRes.status === 403) {
            if (errData.sessionExpired) {
              if (!isAdmin) setShowExpired(true);
              return;
            }
            if (!isAdmin) setShowUpgrade(true);
            return;
          }
          throw new Error(errData.message || "Stream error");
        }
        const streamMsgId = `a-${Date.now()}`;
        setMessages((prev) => [...prev, { _id: streamMsgId, role: "assistant", content: "", timestamp: (/* @__PURE__ */ new Date()).toISOString() }]);
        const reader = streamRes.body.getReader();
        const decoder = new TextDecoder();
        let fullText = "";
        let firstToken = true;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          // Append raw text (preserves newlines/markdown). Strip the [DONE] sentinel.
          let chunk = decoder.decode(value, { stream: true });
          const doneIdx = chunk.indexOf("[DONE]");
          if (doneIdx !== -1) chunk = chunk.slice(0, doneIdx);
          if (chunk) {
            if (firstToken) { setTyping(false); firstToken = false; }
            fullText += chunk;
            const nextText = fullText.replace(/\n\[DONE\]$/, "");
            setMessages((prev) => prev.map((m) => m._id === streamMsgId ? { ...m, content: nextText } : m));
          }
        }
        // Reflect the consumed free message in the UI.
        if (!isAdmin && !isPaid) updateMessageCount((user?.messageCount || 0) + 1);
        if (!activeId) {
          const sessRes = await axios.get("/api/chat/sessions");
          const sessions2 = sessRes.data.sessions || [];
          if (sessions2.length > 0) setActiveId(sessions2[0]._id);
        }
        await loadSessions();
      } else {
        const res = await axios.post("/api/chat/message", { message: txt, sessionId: activeId });
        const { sessionId, message: ai, messageCount, isNewSession, remainingSeconds: srv } = res.data;
        setMessages((prev) => [...prev, { _id: `a-${Date.now()}`, role: "assistant", content: ai, timestamp: ( new Date()).toISOString() }]);
        if (isNewSession) {
          setActiveId(sessionId);
          await loadSessions();
        }
        if (messageCount !== void 0) updateMessageCount(messageCount);
        if (srv !== null && srv !== void 0) setRemainingSecs(srv);
        if (!isAdmin && !isPaid && res.data.messagesRemaining === 0) setTimeout(() => setShowUpgrade(true), 500);
      }
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m._id !== tmpId));
      if (err.response?.status === 403) {
        if (err.response.data?.sessionExpired) {
          if (!isAdmin) setShowExpired(true);
        } else {
          if (!isAdmin) setShowUpgrade(true);
        }
      } else showToast("error", err.response?.data?.message || t("error"));
    } finally {
      setTyping(false);
    }
  }, [input, activeId, isPaid, msgsLeft, isAdmin, user, updateMessageCount, t]);
  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      // Touch keyboards: Enter inserts a newline (WhatsApp-style) — sending is only via the send button.
      if (window.matchMedia("(pointer: coarse)").matches) return;
      e.preventDefault();
      sendMessage();
    }
  };
  const askDelete = (e, id) => {
    e.stopPropagation();
    setConfirmDelete(id);
  };
  const doDelete = async () => {
    try {
      await axios.delete(`/api/chat/session/${confirmDelete}`);
      setSessions((prev) => prev.filter((s) => s._id !== confirmDelete));
      if (activeId === confirmDelete) newChat();
      showToast("success", t("deleted"));
    } catch {
    }
    setConfirmDelete(null);
  };
  const openRename = (e, session) => {
    e.stopPropagation();
    setRenameModal(session._id);
    setRenameVal(session.title || "");
  };
  const doRename = async () => {
    if (!renameVal.trim()) return;
    try {
      await axios.patch(`/api/chat/session/${renameModal}/rename`, { title: renameVal });
      setSessions((prev) => prev.map((s) => s._id === renameModal ? { ...s, title: renameVal } : s));
      setRenameModal(null);
    } catch {
    }
  };
  const doShare = async (e, id) => {
    e.stopPropagation();
    try {
      const r = await axios.post(`/api/chat/session/${id}/share`);
      const { shareUrl } = r.data;
      const tryClipboard = async () => {
        if (!navigator.clipboard?.writeText) return false;
        await navigator.clipboard.writeText(shareUrl);
        return true;
      };
      const tryShareSheet = async () => {
        if (typeof navigator.share !== "function") return null;
        try {
          await navigator.share({
            title: "TaweelAI",
            url: shareUrl,
            text: shareUrl
          });
          return "ok";
        } catch (err) {
          if (err && err.name === "AbortError") return "abort";
          return null;
        }
      };
      const tryExecCopy = () => {
        try {
          const ta = document.createElement("textarea");
          ta.value = shareUrl;
          ta.setAttribute("readonly", "");
          ta.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0";
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          ta.setSelectionRange(0, shareUrl.length);
          const ok = document.execCommand("copy");
          document.body.removeChild(ta);
          return ok;
        } catch {
          return false;
        }
      };
      try {
        if (await tryClipboard()) {
          showToast("success", t("shareLinkCopied"));
          return;
        }
      } catch {
        /* iOS غالبًا يرفض النسخ بعد await — نكمّل بالبدائل */
      }
      const sheet = await tryShareSheet();
      if (sheet === "ok") {
        showToast("success", t("shareCompleted"));
        return;
      }
      if (sheet === "abort") return;
      if (tryExecCopy()) {
        showToast("success", t("shareLinkCopied"));
        return;
      }
      showToast("error", t("shareFailed"));
    } catch {
      showToast("error", t("shareFailed"));
    }
  };
  const timerClass = remainingSecs === null ? "" : remainingSecs < 300 ? "crit" : remainingSecs < 600 ? "warn" : "";
  return <div className="app-layout">
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 199 }} />}
      {}
      <aside
    className={`sidebar ${sidebarOpen ? "open" : ""} ${sidebarCollapsed ? "collapsed" : ""}`}
    style={{ width: sidebarCollapsed ? 64 : void 0, transition: "width .2s ease" }}
  >
        <div className="sidebar-logo" style={sidebarCollapsed ? { position: "relative", padding: "0 10px 2px", justifyContent: "center" } : void 0}>
          {sidebarCollapsed ? <img
    src={getPublicAssetUrl("collapsed-icon.svg")}
    alt=""
    className="logo-img sidebar-collapsed-icon-img"
    draggable={false}
  /> : <Logo
    variant="mark"
    instanceId={`${chatLogoInstanceId}-${logoImageEpoch}`}
    size={LOGO_SIDEBAR_MARK_EXPANDED}
    className="logo-img"
    style={{ borderRadius: 0, background: "transparent" }}
  />}
          <button
    className="sidebar-collapse-icon"
    onClick={() => setSidebarCollapsed((c) => !c)}
    title={sidebarCollapsed ? isAr ? "\u0627\u0641\u062A\u062D \u0627\u0644\u0634\u0631\u064A\u0637 \u0627\u0644\u062C\u0627\u0646\u0628\u064A" : "Open sidebar" : isAr ? "\u0627\u063A\u0644\u0642 \u0627\u0644\u0634\u0631\u064A\u0637 \u0627\u0644\u062C\u0627\u0646\u0628\u064A" : "Close sidebar"}
    aria-label={sidebarCollapsed ? isAr ? "\u0627\u0641\u062A\u062D \u0627\u0644\u0634\u0631\u064A\u0637 \u0627\u0644\u062C\u0627\u0646\u0628\u064A" : "Open sidebar" : isAr ? "\u0627\u063A\u0644\u0642 \u0627\u0644\u0634\u0631\u064A\u0637 \u0627\u0644\u062C\u0627\u0646\u0628\u064A" : "Close sidebar"}
  >
            {sidebarCollapsed ? "\u25B6" : "\u25C0"}
          </button>
        </div>
        <button className="btn-new-chat" onClick={newChat} style={sidebarCollapsed ? { margin: "2px 8px 4px", padding: "9px 0", justifyContent: "center" } : void 0}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
          {!sidebarCollapsed && t("newChat")}
        </button>
        {!sidebarCollapsed && <div className="sidebar-label">{t("searchHistory")}</div>}
        {!sidebarCollapsed && <div className="sidebar-search">
            <span className="search-icon-abs" />
            <input type="text" placeholder={t("searchChats")} value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
          </div>}
        {!sidebarCollapsed && <div className="sidebar-chats">
            {filtered.length === 0 ? <div className="no-chats-msg">{t("noChats")}</div> : filtered.map((s) => <div key={s._id} className={`chat-item ${activeId === s._id ? "active" : ""}`} onClick={() => loadSession(s._id)}>
                <div className="ci-body">
                  <div className="ci-title">{s.title || s.lastMessage?.substring(0, 42) || "Chat"}</div>
                  <div className="ci-date">{fmtDate(s.lastTimestamp, t)}</div>
                </div>
                <div className="ci-actions">
                  <button className="ci-btn" title={t("rename")} aria-label={t("rename")} onClick={(e) => openRename(e, s)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                  </button>
                  <button className="ci-btn red" title={t("deleteChat")} aria-label={t("deleteChat")} onClick={(e) => askDelete(e, s._id)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6M14 11v6" />
                      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>)}
          </div>}
        {sidebarCollapsed && <div style={{ flex: 1, overflowY: "auto", padding: "4px 0" }}>
            {filtered.slice(0, 8).map((s) => <div
    key={s._id}
    onClick={() => loadSession(s._id)}
    title={s.title || "Chat"}
    style={{
      padding: "8px 0",
      cursor: "pointer",
      textAlign: "center",
      fontSize: 14,
      borderRadius: 8,
      margin: "1px 8px",
      background: activeId === s._id ? "var(--accent-glow)" : "transparent",
      border: activeId === s._id ? "1px solid var(--accent-b)" : "1px solid transparent"
    }}
  >
                💬
              </div>)}
          </div>}
        <div className="sidebar-footer" style={sidebarCollapsed ? { padding: "5px 4px" } : void 0}>
          <button
            type="button"
            className="sidebar-theme-toggle"
            onClick={toggleTheme}
            title={theme === "dark" ? (isAr ? "الوضع الفاتح" : "Light mode") : (isAr ? "الوضع الداكن" : "Dark mode")}
            style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%",
              justifyContent: "center",
              padding: sidebarCollapsed ? "11px 0" : "12px 14px", marginBottom: 10,
              background: "var(--bg-card)", border: "1px solid var(--border2)", borderRadius: 14,
              color: "var(--text-1)", cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 600,
              boxShadow: "var(--shadow-s)"
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>{theme === "dark" ? "☀️" : "🌙"}</span>
            {!sidebarCollapsed && <span>{theme === "dark" ? (isAr ? "الوضع الفاتح" : "Light mode") : (isAr ? "الوضع الداكن" : "Dark mode")}</span>}
          </button>
          <div className="profile-menu-wrap sidebar-profile" ref={profileRef}>
            <button
    className="profile-trigger sidebar-profile-trigger"
    onClick={() => {
      setProfileMenuOpen((v) => !v);
      setShowNotifications(false);
    }}
    style={sidebarCollapsed ? { padding: "6px 0", justifyContent: "center", border: "none", background: "transparent" } : void 0}
  >
              {user?.avatar ? <img src={user.avatar} alt="" style={{ width: 32, height: 32, borderRadius: "50%", border: "2px solid var(--border2)" }} /> : <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 12 }}>
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </div>}
              {!sidebarCollapsed && <div className="profile-texts">
                  <span className="profile-name">{user?.name || "User"}</span>
                </div>}
            </button>
            {profileMenuOpen && <div className="profile-dropdown sidebar-profile-dropdown" style={{ position: "relative" }}>
                {!isAdmin && <button type="button" className="profile-dd-btn" onClick={() => {
    setShowUpgrade(true);
    setProfileMenuOpen(false);
  }}>
                    <span className="sidebar-menu-em">{t("upgradePlan")}</span>
                  </button>}
                {}
                <button
    type="button"
    className="profile-dd-btn"
    onClick={() => setShowNotifications((v) => !v)}
    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}
  >
                  <span className="sidebar-menu-em">{t("notifications")}</span>
                  {notifBadge > 0 && <span className="notif-menu-badge">
                      {notifBadge > 9 ? "9+" : notifBadge}
                    </span>}
                </button>
                {showNotifications && <NotificationsPanel
    onClose={() => {
      setShowNotifications(false);
      setNotifBadge(unreadCount());
    }}
    onUnreadChange={syncNotifBadge}
  />}
                <button type="button" className="profile-dd-btn" onClick={() => {
    navigate("/settings");
    setProfileMenuOpen(false);
  }}>
                  <span className="sidebar-menu-em">{t("settings")}</span>
                </button>
                <button type="button" className="profile-dd-btn danger" onClick={async () => {
    await logout();
    navigate("/signin");
  }}>
                  <span className="sidebar-menu-em">{t("logout")}</span>
                </button>
              </div>}
          </div>
        </div>
      </aside>
      {}
      <div className="main-content">
        {}
        {!sidebarOpen && <button
    className="mobile-sidebar-fab"
    onClick={() => setSidebarOpen(true)}
    aria-label="Open sidebar"
    title="Open sidebar"
  >
            ☰
          </button>}
        {}
        <div className="topbar">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          {isAdmin ? <div className="free-pill admin-unlimited-pill" style={{ cursor: "default" }}>{t("adminUnlimited")}</div> : isPaidMonthly ? <div className="timer-pill monthly-pill" style={{ animation: "none", borderColor: "var(--accent-b)", background: "var(--accent-glow)" }}>
              <span style={{ fontSize: 14 }}>👑</span>
              {t("proPlanActive")}
            </div> : isPaidHourly && remainingSecs !== null ? <div className={`timer-pill ${timerClass}`}>
              <span className="t-dot" />
              {t("sessionActive")} · {fmtTimer(remainingSecs)}
            </div> : <div
    className={`free-pill ${msgsLeft <= 3 ? "low" : ""}`}
    onClick={() => msgsLeft === 0 && setShowUpgrade(true)}
    style={{ cursor: msgsLeft === 0 ? "pointer" : "default" }}
  >
              {msgsLeft}/{FREE_LIMIT} {t("messagesRemaining")}
            </div>}
          <div className="topbar-spacer" />
          {activeId && <button className="share-top-btn" onClick={(e) => doShare(e, activeId)} title={t("shareChat")}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 6, flexShrink: 0 }}>
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>
              {t("shareChat")}
            </button>}
        </div>
        {}
        <div
    className={messages.length === 0 ? "chat-area chat-area--empty-welcome" : "chat-area"}
    ref={chatAreaRef}
  >
          <div
    className="chat-area-zoom-inner"
    style={{
      transform: `scale(${chatZoom})`,
      transformOrigin: `${chatZoomOrigin.x}% ${chatZoomOrigin.y}%`,
      minHeight: "100%"
    }}
  >
            {messages.length === 0 ? <div className="welcome-wrap">
                <div className="welcome-logo-area">
                  <Logo
    variant="full"
    instanceId={`${chatLogoInstanceId}-${logoImageEpoch}`}
    size={LOGO_CENTER_CHAT_EMPTY}
    className="welcome-logo-img welcome-logo-responsive"
  />
                  <p className="welcome-tagline">{t("howCanIHelp")}</p>
                </div>
              </div> : <div className="messages-wrap" ref={messagesWrapRef}>
                {messages.map((msg) => <div key={msg._id} className={`msg ${msg.role}`}>
                    <div className="msg-av">
                      {msg.role === "user" ? user?.avatar ? <img src={user.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} /> : (user?.name?.[0] || "U").toUpperCase() : <img src={getPublicAssetUrl("small-icon.svg")} alt="" className="msg-assistant-model-icon" draggable={false} />}
                    </div>
                    <div className="msg-body">
                      <div className="msg-bubble" dir={isAr ? "rtl" : "ltr"}>
                        <MarkdownMessage content={msg.content} isUser={msg.role === "user"} />
                      </div>
                      <div className="msg-time">{fmtTime(msg.timestamp)}</div>
                    </div>
                  </div>)}
                {typing && <div className="msg assistant">
                    <div className="msg-av">
                      <img src={getPublicAssetUrl("small-icon.svg")} alt="" className="msg-assistant-model-icon" draggable={false} />
                    </div>
                    <div className="msg-body">
                      <div className="msg-bubble"><div className="typing-dots"><span /><span /><span /></div></div>
                    </div>
                  </div>}
                <div ref={endRef} className="messages-end-spacer" />
              </div>}
          </div>
        </div>
        {}
        <div className="input-area">
          <div className="input-shell">
            <textarea
    ref={inputRef}
    className="chat-ta"
    placeholder={t("typeMessage")}
    value={input}
    readOnly={!composerUnlocked}
    onPointerDown={(e) => {
      if (!composerUnlocked) {
        e.currentTarget.readOnly = false;
        setComposerUnlocked(true);
      }
    }}
    onChange={(e) => {
      setInput(e.target.value);
      e.target.style.height = "auto";
      e.target.style.height = Math.min(e.target.scrollHeight, 130) + "px";
    }}
    onKeyDown={handleKey}
    rows={1}
    dir={isAr ? "rtl" : "ltr"}
    enterKeyHint="enter"
  />
            <button className="send-btn" onClick={() => sendMessage()} disabled={!input.trim() || typing}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z" /></svg>
            </button>
          </div>
          <div className="input-disclaimer">{t("inputDisclaimer")}</div>
        </div>
      </div>
      {}
      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} onSuccess={() => setShowUpgrade(false)} />}
      {showExpired && <SessionExpiredModal onRenewed={() => {
    setShowExpired(false);
    setRemainingSecs(3600);
  }} />}
      {}
      {confirmDelete && <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmDelete(null)}>
          <div className="modal-box confirm-box">
            <div className="modal-icon" aria-hidden="true">
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
              </svg>
            </div>
            <h3 className="modal-title">{t("confirmDelete")}</h3>
            <p className="confirm-msg">{t("confirmDeleteMsg")}</p>
            <div className="confirm-btns">
              <button className="btn-ghost" onClick={() => setConfirmDelete(null)}>{t("cancel")}</button>
              <button className="btn-danger" onClick={doDelete}>{t("deleteForever")}</button>
            </div>
          </div>
        </div>}
      {}
      {renameModal && <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setRenameModal(null)}>
          <div className="modal-box rename-box">
            <h3 className="modal-title" style={{ marginBottom: 18 }}>{t("renameChat")}</h3>
            <input
    className="rename-input"
    value={renameVal}
    onChange={(e) => setRenameVal(e.target.value)}
    placeholder={t("newTitle")}
    onKeyDown={(e) => e.key === "Enter" && doRename()}
  />
            <div className="rename-actions">
              <button className="btn-ghost" onClick={() => setRenameModal(null)}>{t("cancel")}</button>
              <button className="btn-save" onClick={doRename}>{t("save")}</button>
            </div>
          </div>
        </div>}
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </div>;
};
var stdin_default = Chat;
export {
  stdin_default as default
};
